import React, { useState, useEffect } from 'react'
import { Search, TrendingUp, DollarSign, Building, RefreshCw, ChevronDown, ChevronUp, Package, Save, Check, X, AlertCircle, Download } from 'lucide-react'
import { HubSpotService, LoadingProgress } from '../services/hubspotService'
import { DealSummary, DealStats, AssociationLabel } from '../types'
import { PieChart } from './PieChart'

export const AstonFundersDashboard: React.FC = () => {
  const [deals, setDeals] = useState<DealSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingProgress, setLoadingProgress] = useState<LoadingProgress | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [labelOptions, setLabelOptions] = useState<string[]>(['Unlabeled'])
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredDeals, setFilteredDeals] = useState<DealSummary[]>([])
  const [stats, setStats] = useState<DealStats | null>(null)
  const [sortField, setSortField] = useState<'closeDate' | 'amount'>('closeDate')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const [editingAssociation, setEditingAssociation] = useState<{ dealId: string; companyId: string } | null>(null)
  const [activeTab, setActiveTab] = useState<'deals' | 'analytics'>('deals')
  const [showOnlyNoFunders, setShowOnlyNoFunders] = useState(false)
  const [savingAssociations, setSavingAssociations] = useState<Set<string>>(new Set())
  const [saveResults, setSaveResults] = useState<Map<string, { success: boolean; message?: string }>>(new Map())

  const hubspotService = HubSpotService.getInstance()

  const loadAssociationLabels = async () => {
    try {
      const labels = await hubspotService.fetchAvailableAssociationLabels()
      setLabelOptions(labels.map(label => label.label))
    } catch (error) {
      console.error('Failed to load association labels:', error)
      // Use fallback labels
      setLabelOptions(['Unlabeled', 'Primary'])
    }
  }

  useEffect(() => {
    loadAssociationLabels()
    loadDeals()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    filterAndSortDeals()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deals, searchTerm, sortField, sortDirection, showOnlyNoFunders])

  const loadDeals = async () => {
    setLoading(true)
    setLoadingProgress(null)
    setError(null)
    try {
      // Get all closed won deals with progress tracking
      const dealsToUse = await hubspotService.getClosedWonDeals(500, (progress: LoadingProgress) => {
        setLoadingProgress(progress)
      })

      const formattedDeals = dealsToUse.map(dwc => hubspotService.formatDealSummary(dwc))
      setDeals(formattedDeals)
      
      const calculatedStats = hubspotService.calculateStats(formattedDeals)
      setStats(calculatedStats)
    } catch (err) {
      console.error('Error loading deals:', err)
      setError('Failed to load deals from HubSpot. Please check your connection and try again.')
    } finally {
      setLoading(false)
      setLoadingProgress(null)
    }
  }

  const filterAndSortDeals = () => {
    let filtered = [...deals]

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(deal =>
        deal.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        deal.companies.some(c => c.name?.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    }

    // Apply no funders filter
    if (showOnlyNoFunders) {
      filtered = filtered.filter(deal => {
        // If this deal has unsaved changes, keep it visible so user can save
        const hasUnsavedChanges = hubspotService.hasUnsavedChanges(deal.id)
        if (hasUnsavedChanges) {
          return true // Always show deals with unsaved changes
        }

        // Check if any company in this deal is labeled as "Funder"
        const hasFunder = deal.companies.some(company => {
          const label = hubspotService.getAssociationLabel(deal.id, company.id, company.associationLabel)
          return label === 'Funder'
        })
        return !hasFunder // Only include deals that have NO funder
      })
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = sortField === 'closeDate' ? a.closeDate.getTime() : a.amount
      const bValue = sortField === 'closeDate' ? b.closeDate.getTime() : b.amount
      
      if (sortDirection === 'asc') {
        return aValue - bValue
      } else {
        return bValue - aValue
      }
    })

    setFilteredDeals(filtered)
  }

  const handleSort = (field: 'closeDate' | 'amount') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const formatCompactCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return `£${(amount / 1000000).toFixed(1)}M`
    } else if (amount >= 1000) {
      return `£${(amount / 1000).toFixed(0)}K`
    }
    return formatCurrency(amount)
  }

  const handleAssociationChange = (dealId: string, companyId: string, newLabel: AssociationLabel) => {
    hubspotService.updateAssociationLabel(dealId, companyId, newLabel)
    setEditingAssociation(null)
    
    // Update the deals with the new association label
    const updatedDeals = deals.map(deal => {
      if (deal.id === dealId) {
        return {
          ...deal,
          companies: deal.companies.map(company => 
            company.id === companyId 
              ? { ...company, associationLabel: newLabel === 'Unlabeled' ? undefined : newLabel }
              : company
          )
        }
      }
      return deal
    })
    
    setDeals(updatedDeals)
    const newStats = hubspotService.calculateStats(updatedDeals)
    setStats(newStats)
  }

  const getAssociationColor = (label?: string) => {
    switch (label) {
      case 'Primary':
        return { bg: '#fef3c7', text: '#d97706', border: '#fde68a' }
      case 'Funder':
        return { bg: '#dcfce7', text: '#166534', border: '#bbf7d0' }
      case 'Supplier':
        return { bg: '#dbeafe', text: '#1e40af', border: '#bfdbfe' }
      default:
        return { bg: '#f3f4f6', text: '#6b7280', border: '#e5e7eb' }
    }
  }

  const handleDownloadCSV = () => {
    // Generate CSV content
    const headers = [
      'Close Date',
      'Deal Name',
      'Deal Amount',
      'Deal ID',
      'Company Name',
      'Company Domain',
      'Association Label'
    ]

    const rows: string[][] = []

    filteredDeals.forEach(deal => {
      if (deal.companies.length === 0) {
        // Deal with no companies
        rows.push([
          deal.formattedDate,
          `"${deal.name.replace(/"/g, '""')}"`,
          deal.amount.toString(),
          deal.id,
          '',
          '',
          ''
        ])
      } else {
        // Deal with companies
        deal.companies.forEach(company => {
          const associationLabel = hubspotService.getAssociationLabel(
            deal.id,
            company.id,
            company.associationLabel
          )
          rows.push([
            deal.formattedDate,
            `"${deal.name.replace(/"/g, '""')}"`,
            deal.amount.toString(),
            deal.id,
            `"${(company.name || '').replace(/"/g, '""')}"`,
            `"${(company.domain || '').replace(/"/g, '""')}"`,
            associationLabel || 'Unlabeled'
          ])
        })
      }
    })

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n')

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `aston-funders-${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleSaveAssociations = async (dealId: string) => {
    const unsavedChanges = hubspotService.getUnsavedChangesForDeal(dealId)
    if (unsavedChanges.length === 0) return

    setSavingAssociations(prev => new Set(Array.from(prev).concat([dealId])))
    const newResults = new Map(saveResults)

    try {
      let allSuccessful = true
      let errorMessages: string[] = []

      for (const { companyId, newLabel } of unsavedChanges) {
        const result = await hubspotService.saveAssociationToHubSpot(dealId, companyId, newLabel as AssociationLabel)
        if (!result.success) {
          allSuccessful = false
          if (result.message) errorMessages.push(result.message)
        }
      }

      if (allSuccessful) {
        newResults.set(dealId, { success: true })
        // Trigger filter update after successful save
        setTimeout(() => {
          filterAndSortDeals()
        }, 100)
        // Refresh the specific deal to get updated data from HubSpot
        setTimeout(() => {
          newResults.delete(dealId)
          setSaveResults(new Map(newResults))
        }, 3000)
      } else {
        newResults.set(dealId, { 
          success: false, 
          message: errorMessages.length > 0 ? errorMessages.join('; ') : 'Failed to save some associations' 
        })
        setTimeout(() => {
          newResults.delete(dealId)
          setSaveResults(new Map(newResults))
        }, 5000)
      }
    } catch (error) {
      newResults.set(dealId, { 
        success: false, 
        message: 'Unexpected error occurred while saving' 
      })
      setTimeout(() => {
        newResults.delete(dealId)
        setSaveResults(new Map(newResults))
      }, 5000)
    } finally {
      setSaveResults(newResults)
      setSavingAssociations(prev => {
        const updated = new Set(prev)
        updated.delete(dealId)
        return updated
      })
    }
  }

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f9fafb'
      }}>
        <div style={{ textAlign: 'center', maxWidth: '400px' }}>
          <div style={{
            animation: 'spin 1s linear infinite',
            borderRadius: '50%',
            height: '48px',
            width: '48px',
            border: '4px solid #3b82f6',
            borderBottomColor: 'transparent',
            margin: '0 auto 16px'
          }}></div>
          <p style={{ color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif', fontSize: '16px', marginBottom: '8px' }}>
            {loadingProgress ? loadingProgress.message : 'Loading Aston deals from HubSpot...'}
          </p>
          {loadingProgress && (
            <>
              <div style={{
                background: '#e5e7eb',
                borderRadius: '8px',
                height: '8px',
                overflow: 'hidden',
                marginBottom: '8px'
              }}>
                <div style={{
                  background: '#3b82f6',
                  height: '100%',
                  borderRadius: '8px',
                  transition: 'width 0.3s ease',
                  width: `${loadingProgress.total > 0 ? (loadingProgress.current / loadingProgress.total) * 100 : 0}%`
                }}></div>
              </div>
              <p style={{ 
                color: '#9ca3af', 
                fontFamily: 'Lexend Deca, sans-serif', 
                fontSize: '14px',
                margin: '0'
              }}>
                {loadingProgress.phase === 'deals' ? 'Finding deals...' : 
                 loadingProgress.phase === 'companies' ? `Processing ${loadingProgress.current} of ${loadingProgress.total} deals` :
                 'Almost done...'}
              </p>
            </>
          )}
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f9fafb'
      }}>
        <div style={{
          background: 'white',
          padding: '32px',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          maxWidth: '500px',
          textAlign: 'center'
        }}>
          <div style={{
            color: '#ef4444',
            marginBottom: '16px',
            fontSize: '48px'
          }}>⚠️</div>
          <h2 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#111827',
            marginBottom: '12px',
            fontFamily: 'Lexend Deca, sans-serif'
          }}>
            Error Loading Deals
          </h2>
          <p style={{
            color: '#6b7280',
            marginBottom: '24px',
            fontFamily: 'Lexend Deca, sans-serif'
          }}>
            {error}
          </p>
          <button
            onClick={loadDeals}
            style={{
              padding: '12px 24px',
              background: '#3b82f6',
              color: 'white',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              fontFamily: 'Lexend Deca, sans-serif',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#2563eb'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#3b82f6'}
          >
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (!stats) return null

  // Prepare data for pie charts
  const fundersByValueData = [
    ...stats.fundersByValue.map(funder => ({
      label: funder.name,
      value: funder.value
    })),
    // Add "No Funder" category if there are deals without funders
    ...(stats.dealsWithoutFunder.count > 0 ? [{
      label: 'No Funder',
      value: stats.dealsWithoutFunder.totalValue
    }] : [])
  ]

  const fundersByCountData = [
    ...stats.fundersByCount.map(funder => ({
      label: funder.name,
      value: funder.dealCount
    })),
    // Add "No Funder" category if there are deals without funders
    ...(stats.dealsWithoutFunder.count > 0 ? [{
      label: 'No Funder',
      value: stats.dealsWithoutFunder.count
    }] : [])
  ]

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e5e7eb',
        padding: '24px'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px'
          }}>
            <div>
              <h1 style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#111827',
                fontFamily: 'Lexend Deca, sans-serif',
                marginBottom: '8px'
              }}>
                Aston Funders Dashboard
              </h1>
              <p style={{
                color: '#6b7280',
                fontFamily: 'Lexend Deca, sans-serif',
                fontSize: '16px'
              }}>
                Closed won deals with funder analysis
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleDownloadCSV}
                style={{
                  padding: '10px 16px',
                  background: '#10b981',
                  color: 'white',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'Lexend Deca, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#059669'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#10b981'}
              >
                <Download size={16} />
                Download CSV
              </button>
              <button
                onClick={loadDeals}
                style={{
                  padding: '10px 16px',
                  background: '#3b82f6',
                  color: 'white',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'Lexend Deca, sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#2563eb'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#3b82f6'}
              >
                <RefreshCw size={16} />
                Refresh
              </button>
            </div>
          </div>

          {/* KPI Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <Package style={{ height: '20px', width: '20px', color: '#10b981' }} />
                <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif', margin: 0 }}>
                  Total Deals
                </h3>
              </div>
              <p style={{ fontSize: '24px', fontWeight: '700', color: '#111827', fontFamily: 'Lexend Deca, sans-serif', margin: 0 }}>
                {stats.totalDeals.toLocaleString()}
              </p>
            </div>

            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <DollarSign style={{ height: '20px', width: '20px', color: '#3b82f6' }} />
                <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif', margin: 0 }}>
                  Total Value
                </h3>
              </div>
              <p style={{ fontSize: '24px', fontWeight: '700', color: '#111827', fontFamily: 'Lexend Deca, sans-serif', margin: 0 }}>
                {formatCurrency(stats.totalAmount)}
              </p>
            </div>

            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <TrendingUp style={{ height: '20px', width: '20px', color: '#f59e0b' }} />
                <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif', margin: 0 }}>
                  Avg Deal Size
                </h3>
              </div>
              <p style={{ fontSize: '24px', fontWeight: '700', color: '#111827', fontFamily: 'Lexend Deca, sans-serif', margin: 0 }}>
                {formatCurrency(stats.averageDealSize)}
              </p>
            </div>

            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <Building style={{ height: '20px', width: '20px', color: '#8b5cf6' }} />
                <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif', margin: 0 }}>
                  Companies as Funders
                </h3>
              </div>
              <p style={{ fontSize: '24px', fontWeight: '700', color: '#111827', fontFamily: 'Lexend Deca, sans-serif', margin: 0 }}>
                {stats.fundersByValue.length}
              </p>
            </div>

            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <AlertCircle style={{ height: '20px', width: '20px', color: '#ef4444' }} />
                <h3 style={{ fontSize: '14px', fontWeight: '500', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif', margin: 0 }}>
                  Deals Without Funders
                </h3>
              </div>
              <p style={{ fontSize: '24px', fontWeight: '700', color: '#111827', fontFamily: 'Lexend Deca, sans-serif', margin: 0 }}>
                {stats.dealsWithoutFunder.count}
              </p>
              <p style={{ fontSize: '14px', color: '#6b7280', fontFamily: 'Lexend Deca, sans-serif', margin: '4px 0 0 0' }}>
                {formatCurrency(stats.dealsWithoutFunder.totalValue)} total value
              </p>
            </div>
          </div>

          {/* Tabs */}
          <div style={{
            display: 'flex',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <button
              onClick={() => setActiveTab('deals')}
              style={{
                padding: '12px 16px',
                background: 'transparent',
                color: activeTab === 'deals' ? '#2563eb' : '#6b7280',
                fontWeight: activeTab === 'deals' ? '600' : '500',
                border: 'none',
                borderBottom: activeTab === 'deals' ? '2px solid #2563eb' : '2px solid transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'Lexend Deca, sans-serif',
                transition: 'all 0.2s'
              }}
            >
              Deals
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              style={{
                padding: '12px 16px',
                background: 'transparent',
                color: activeTab === 'analytics' ? '#2563eb' : '#6b7280',
                fontWeight: activeTab === 'analytics' ? '600' : '500',
                border: 'none',
                borderBottom: activeTab === 'analytics' ? '2px solid #2563eb' : '2px solid transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontFamily: 'Lexend Deca, sans-serif',
                transition: 'all 0.2s'
              }}
            >
              Funder Analytics
            </button>
          </div>

          {/* Search Bar - only show for deals tab */}
          {activeTab === 'deals' && (
            <div style={{ marginTop: '16px' }}>
              <div style={{
                position: 'relative',
                maxWidth: '500px'
              }}>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search deals and companies..."
                  style={{
                    width: '100%',
                    paddingLeft: '44px',
                    paddingRight: '16px',
                    paddingTop: '12px',
                    paddingBottom: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontFamily: 'Lexend Deca, sans-serif',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
                <Search style={{
                  position: 'absolute',
                  left: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  height: '18px',
                  width: '18px',
                  color: '#9ca3af'
                }} />
              </div>

              {/* No Funders Filter */}
              <div style={{ marginTop: '12px' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  fontFamily: 'Lexend Deca, sans-serif',
                  color: '#374151',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}>
                  <input
                    type="checkbox"
                    checked={showOnlyNoFunders}
                    onChange={(e) => setShowOnlyNoFunders(e.target.checked)}
                    style={{
                      width: '16px',
                      height: '16px',
                      marginRight: '4px',
                      cursor: 'pointer'
                    }}
                  />
                  <AlertCircle size={16} style={{ color: '#ef4444' }} />
                  List Deals with No Funders
                  {showOnlyNoFunders && stats && (
                    <span style={{
                      marginLeft: '8px',
                      padding: '2px 6px',
                      background: '#fef2f2',
                      color: '#dc2626',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {stats.dealsWithoutFunder.count} deals
                    </span>
                  )}
                </label>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '24px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {activeTab === 'deals' && (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              {/* Table Header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 3fr 1fr',
                padding: '16px 20px',
                background: '#f9fafb',
                borderBottom: '2px solid #e5e7eb',
                fontWeight: '600',
                fontSize: '14px',
                color: '#374151',
                fontFamily: 'Lexend Deca, sans-serif'
              }}>
                <div 
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                  onClick={() => handleSort('closeDate')}
                >
                  Close Date
                  {sortField === 'closeDate' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                </div>
                <div>Deal Name</div>
                <div 
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                  onClick={() => handleSort('amount')}
                >
                  Amount
                  {sortField === 'amount' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                </div>
              </div>

              {/* Table Body */}
              <div>
                {filteredDeals.length === 0 ? (
                  <div style={{
                    padding: '48px',
                    textAlign: 'center',
                    color: '#6b7280',
                    fontFamily: 'Lexend Deca, sans-serif'
                  }}>
                    {searchTerm ? 'No deals found matching your search.' : 'No closed won deals found.'}
                  </div>
                ) : (
                  filteredDeals.map((deal) => {
                    const hasUnsavedChanges = hubspotService.hasUnsavedChanges(deal.id)
                    const isSaving = savingAssociations.has(deal.id)
                    const saveResult = saveResults.get(deal.id)
                    
                    return (
                      <div key={deal.id}>
                        {/* Main Deal Row */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 3fr 1fr',
                          padding: '16px 20px',
                          borderBottom: deal.companies.length > 0 || hasUnsavedChanges || saveResult ? '1px solid #f3f4f6' : '1px solid #e5e7eb',
                          fontSize: '14px',
                          color: '#111827',
                          fontFamily: 'Lexend Deca, sans-serif',
                          transition: 'background 0.2s'
                        }}>
                          <div>{deal.formattedDate}</div>
                          <div>
                            <a 
                              href={`https://app.hubspot.com/contacts/47780591/record/0-3/${deal.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ 
                                fontWeight: '500', 
                                color: '#2563eb',
                                textDecoration: 'none',
                                transition: 'color 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = '#1d4ed8'
                                e.currentTarget.style.textDecoration = 'underline'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = '#2563eb'
                                e.currentTarget.style.textDecoration = 'none'
                              }}
                            >
                              {deal.name}
                            </a>
                          </div>
                          <div style={{ fontWeight: '600', color: '#10b981' }}>{deal.formattedAmount}</div>
                        </div>

                        {/* Save Button Row */}
                        {hasUnsavedChanges && (
                          <div style={{
                            padding: '8px 20px',
                            background: '#fffbeb',
                            borderBottom: '1px solid #fbbf24',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}>
                            <span style={{
                              fontSize: '14px',
                              color: '#92400e',
                              fontFamily: 'Lexend Deca, sans-serif'
                            }}>
                              You have unsaved association changes
                            </span>
                            <button
                              onClick={() => handleSaveAssociations(deal.id)}
                              disabled={isSaving}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '6px 12px',
                                background: isSaving ? '#f3f4f6' : '#10b981',
                                color: isSaving ? '#6b7280' : 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: '500',
                                fontFamily: 'Lexend Deca, sans-serif',
                                cursor: isSaving ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                if (!isSaving) e.currentTarget.style.background = '#059669'
                              }}
                              onMouseLeave={(e) => {
                                if (!isSaving) e.currentTarget.style.background = '#10b981'
                              }}
                            >
                              {isSaving ? (
                                <>
                                  <div style={{
                                    width: '14px',
                                    height: '14px',
                                    border: '2px solid #9ca3af',
                                    borderBottomColor: 'transparent',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite'
                                  }} />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Save size={14} />
                                  Save to HubSpot
                                </>
                              )}
                            </button>
                          </div>
                        )}

                        {/* Save Result Feedback */}
                        {saveResult && (
                          <div style={{
                            padding: '8px 20px',
                            background: saveResult.success ? '#f0fdf4' : '#fef2f2',
                            borderBottom: `1px solid ${saveResult.success ? '#22c55e' : '#ef4444'}`,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            {saveResult.success ? (
                              <Check size={16} style={{ color: '#22c55e' }} />
                            ) : (
                              <X size={16} style={{ color: '#ef4444' }} />
                            )}
                            <span style={{
                              fontSize: '14px',
                              color: saveResult.success ? '#166534' : '#dc2626',
                              fontFamily: 'Lexend Deca, sans-serif'
                            }}>
                              {saveResult.success 
                                ? 'Association changes saved successfully to HubSpot!'
                                : `Failed to save: ${saveResult.message || 'Unknown error'}`
                              }
                            </span>
                          </div>
                        )}

                      {/* Companies Sub-row */}
                      {deal.companies.length > 0 && (
                        <div style={{
                          padding: '12px 20px 16px 20px',
                          background: '#f9fafb',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '8px',
                            alignItems: 'flex-start'
                          }}>
                            {deal.companies.map((company, idx) => {
                              const colors = getAssociationColor(company.associationLabel)
                              const isEditing = editingAssociation?.dealId === deal.id && 
                                               editingAssociation?.companyId === company.id
                              
                              return (
                                <div key={idx} style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '6px 12px',
                                  background: 'white',
                                  borderRadius: '6px',
                                  border: '1px solid #e5e7eb',
                                  fontSize: '14px',
                                  fontFamily: 'Lexend Deca, sans-serif'
                                }}>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <span style={{
                                      color: '#111827',
                                      fontWeight: '500'
                                    }}>
                                      {company.name}
                                    </span>
                                    {company.domain && (
                                      <span style={{
                                        color: '#6b7280',
                                        fontSize: '12px',
                                        fontWeight: '400'
                                      }}>
                                        {company.domain}
                                      </span>
                                    )}
                                    {company.type && (
                                      <span style={{
                                        color: '#059669',
                                        fontSize: '11px',
                                        fontWeight: '500',
                                        background: '#ecfdf5',
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        alignSelf: 'flex-start'
                                      }}>
                                        {company.type}
                                      </span>
                                    )}
                                  </div>
                                  
                                  {isEditing ? (
                                    <div style={{ position: 'relative' }}>
                                      <select
                                        value={company.associationLabel || 'Unlabeled'}
                                        onChange={(e) => handleAssociationChange(
                                          deal.id, 
                                          company.id, 
                                          e.target.value as AssociationLabel
                                        )}
                                        onBlur={() => setEditingAssociation(null)}
                                        autoFocus
                                        style={{
                                          padding: '2px 20px 2px 8px',
                                          borderRadius: '12px',
                                          fontSize: '12px',
                                          fontWeight: '500',
                                          background: colors.bg,
                                          color: colors.text,
                                          border: `1px solid ${colors.border}`,
                                          appearance: 'none',
                                          cursor: 'pointer',
                                          fontFamily: 'Lexend Deca, sans-serif'
                                        }}
                                      >
                                        {labelOptions.map(option => (
                                          <option key={option} value={option}>
                                            {option}
                                          </option>
                                        ))}
                                      </select>
                                      <ChevronDown style={{
                                        position: 'absolute',
                                        right: '4px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        height: '12px',
                                        width: '12px',
                                        color: colors.text,
                                        pointerEvents: 'none'
                                      }} />
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => setEditingAssociation({ 
                                        dealId: deal.id, 
                                        companyId: company.id 
                                      })}
                                      style={{
                                        padding: '2px 8px',
                                        borderRadius: '12px',
                                        fontSize: '12px',
                                        fontWeight: '500',
                                        background: colors.bg,
                                        color: colors.text,
                                        border: `1px solid ${colors.border}`,
                                        cursor: 'pointer',
                                        fontFamily: 'Lexend Deca, sans-serif',
                                        transition: 'all 0.2s'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.opacity = '0.8'
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.opacity = '1'
                                      }}
                                    >
                                      {company.associationLabel || 'Unlabeled'}
                                    </button>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    )
                  })
                )}
              </div>

              {/* Results Summary */}
              {filteredDeals.length > 0 && (
                <div style={{
                  padding: '16px',
                  textAlign: 'center',
                  color: '#6b7280',
                  fontSize: '14px',
                  fontFamily: 'Lexend Deca, sans-serif',
                  borderTop: '1px solid #f3f4f6'
                }}>
                  Showing {filteredDeals.length} of {deals.length} deals
                </div>
              )}
            </div>
          )}

          {activeTab === 'analytics' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '24px' }}>
              {/* Funders by Value Pie Chart */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
              }}>
                <PieChart
                  data={fundersByValueData}
                  title="Companies Labeled as 'Funder' by Deal Value"
                  formatValue={formatCompactCurrency}
                />
              </div>

              {/* Funders by Count Pie Chart */}
              <div style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
              }}>
                <PieChart
                  data={fundersByCountData}
                  title="Companies Labeled as 'Funder' by Deal Count"
                  formatValue={(value) => `${value} deals`}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
